import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useInquiriesWorkspace from "../components/inquiriesComponents/hooks/useInquiriesWorkspace";
import useKeyboardNav from "../components/leadsComponents/hooks/useKeyboardNav";
import { SMART_VIEWS } from "../components/inquiriesComponents/constants";
import MetricStrip from "../components/inquiriesComponents/workspace/MetricStrip";
import InquiryRail from "../components/inquiriesComponents/workspace/InquiryRail";
import InquiryLedger from "../components/inquiriesComponents/workspace/InquiryLedger";
// Drawer kept in the codebase for fallback / reference; the active UI uses
// the modal below. Do not delete.
// eslint-disable-next-line no-unused-vars
import InquiryDetailDrawer from "../components/inquiriesComponents/workspace/InquiryDetailDrawer";
import InquiryReplyModal from "../components/inquiriesComponents/workspace/InquiryReplyModal";
import TemplateManagerModal from "../components/inquiriesComponents/workspace/TemplateManagerModal";
import AnalyticsView from "../components/inquiriesComponents/workspace/AnalyticsView";

export default function InquiriesDashboard() {
  const navigate = useNavigate();
  const ws = useInquiriesWorkspace();
  const [panel, setPanel] = useState("list");
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const gmailConnected = Boolean(
    ws.adminInfo?.gmailConnected || ws.adminInfo?.automationAllowed
  );

  // When the selected inquiry disappears (filter changed, was closed), clear.
  useEffect(() => {
    if (!ws.selectedId) return;
    if (!ws.filtered.some((i) => i._id === ws.selectedId)) {
      const first = ws.filtered[0];
      if (first) ws.selectInquiry(first._id);
      else ws.clearSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.filtered, ws.selectedId]);

  const viewShortcuts = useMemo(() => {
    const map = {};
    for (const v of SMART_VIEWS) {
      map[v.shortcut] = () => {
        setPanel("list");
        ws.setView(v.id);
      };
    }
    return map;
  }, [ws]);

  useKeyboardNav(
    {
      ...viewShortcuts,
      j: () => ws.selectNext(),
      k: () => ws.selectPrevious(),
      arrowdown: () => ws.selectNext(),
      arrowup: () => ws.selectPrevious(),
      enter: () => {
        if (!ws.selectedId && ws.filtered[0]) ws.selectInquiry(ws.filtered[0]._id);
      },
      escape: () => {
        if (ws.selectedId) ws.clearSelection();
        else if (panel !== "list") setPanel("list");
      },
      "/": () => {
        const el = document.querySelector("[data-inq-search]");
        if (el) el.focus();
      },
      r: () => ws.refresh(),
      a: () => setPanel((p) => (p === "analytics" ? "list" : "analytics")),
    },
    { enabled: true }
  );

  const { selectedId: wsSelectedId, current: wsCurrent, inquiries: wsInquiries } = ws;
  const selectedInquiry = useMemo(() => {
    if (!wsSelectedId) return null;
    const listCopy = wsInquiries.find((i) => i._id === wsSelectedId) || null;
    if (!wsCurrent || wsCurrent._id !== wsSelectedId) return listCopy;
    if (!listCopy) return wsCurrent;

    const listHasMoreThread =
      (listCopy.messages || []).length > (wsCurrent.messages || []).length ||
      (listCopy.responses || []).length > (wsCurrent.responses || []).length;
    if (listHasMoreThread) return listCopy;

    const currentMs = wsCurrent.updatedAt ? new Date(wsCurrent.updatedAt).getTime() : 0;
    const listMs = listCopy.updatedAt ? new Date(listCopy.updatedAt).getTime() : 0;
    // The polling list can receive fresher inbound Gmail replies while the
    // drawer is open. Prefer the newest copy so the conversation thread does
    // not get stuck on the initial `/inquiries/:id` fetch.
    return listMs > currentMs ? listCopy : wsCurrent;
  }, [wsSelectedId, wsCurrent, wsInquiries]);

  const handleMarkReplied = async (id) => {
    await ws.patchInquiry(id, { replied: true });
  };
  const handleToggleClosed = async (id, closed) => {
    await ws.patchInquiry(id, { closed });
  };
  const handleChangeType = async (id, type) => {
    await ws.patchInquiry(id, { type });
  };

  const handleSendReply = async (payload) => {
    const res = await ws.sendReply(payload);
    if (res?.error) throw res.error;
    return res;
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-zinc-50">
      <header className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-white px-4 py-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
            Inquiries workspace
          </p>
          <h1 className="text-base font-semibold text-zinc-900">
            Guest, host, and partner messages
          </h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setPanel((p) => (p === "analytics" ? "list" : "analytics"))}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
              panel === "analytics"
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {panel === "analytics" ? "List view" : "Analytics"}
          </button>
          <button
            onClick={() => ws.refresh()}
            className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            disabled={ws.refreshing}
          >
            {ws.refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {!gmailConnected ? (
            <button
              onClick={() => navigate("/settings")}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              Connect Gmail
            </button>
          ) : null}
        </div>
      </header>

      <div className="border-b border-zinc-200 bg-white px-4 py-2">
        <MetricStrip analytics={ws.analytics} />
      </div>

      <div className="flex min-h-0 flex-1">
        <InquiryRail
          view={ws.view}
          onViewChange={(v) => {
            ws.setView(v);
            setPanel("list");
          }}
          counts={ws.counts}
          analytics={ws.analytics}
          typeFilter={ws.typeFilter}
          onTypeChange={ws.setTypeFilter}
          onOpenAnalytics={() => setPanel("analytics")}
          onRefresh={ws.refresh}
          refreshing={ws.refreshing}
        />

        {panel === "analytics" ? (
          <AnalyticsView
            inquiries={ws.inquiries}
            analytics={ws.analytics}
            onClose={() => setPanel("list")}
          />
        ) : (
          <InquiryLedger
            inquiries={ws.filtered}
            loading={ws.loading}
            view={ws.view}
            search={ws.search}
            onSearchChange={ws.setSearch}
            selectedId={ws.selectedId}
            onSelect={ws.selectInquiry}
            onMarkReplied={handleMarkReplied}
            onToggleClosed={handleToggleClosed}
            onChangeType={handleChangeType}
            sortBy={ws.sortBy}
            onSortChange={ws.setSortBy}
            onOpenTemplates={() => setTemplatesOpen(true)}
          />
        )}
      </div>

      <InquiryReplyModal
        open={!!selectedInquiry}
        inquiry={selectedInquiry}
        loading={ws.loading}
        adminInfo={ws.adminInfo}
        gmailConnected={gmailConnected}
        onConnectGmail={() => navigate("/settings")}
        onClose={ws.clearSelection}
        onPatchInquiry={ws.patchInquiry}
        onSendReply={handleSendReply}
        onNext={ws.selectNext}
        onPrevious={ws.selectPrevious}
        onManageTemplates={() => setTemplatesOpen(true)}
      />

      <TemplateManagerModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
      />
    </div>
  );
}
